import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import AuthContext from '../../../context/authContext';
import useInternalBooking from '../../../hooks/useInternalBooking';
import { getNoShowInvoiceErrorMessage } from '../../../util/noShowInvoiceErrors';
import {
  hasNoShowInvoiceBillableLineItems,
  isNoShowInvoiceSendable,
  NO_SHOW_GRACE_MINUTES_AFTER_START,
} from '../../../util/dateUtils';

export function hasNoShowInvoiceAdyenMerchant(booking) {
  const merchant = booking?.branchId?.adyenMerchant;
  return typeof merchant === 'string' ? merchant.trim().length > 0 : Boolean(merchant);
}

function NoShowInvoiceButton({
  booking,
  onAfterSend,
  variant = 'outlined',
  size = 'small',
  fullWidth = false,
  sx = undefined,
}) {
  const authCtx = useContext(AuthContext);
  const { sendNoShowInvoice, resendNoShowInvoiceMail } = useInternalBooking();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const hasInsurance = useMemo(() => {
    if (!booking) return false;
    const tagHit = Array.isArray(booking.tags) && booking.tags.includes('hasInsurance');
    const pkgHit =
      Array.isArray(booking.selectedPackage?.tags) &&
      booking.selectedPackage.tags.includes('hasInsurance');
    const upsellHit =
      Array.isArray(booking.upsells) &&
      booking.upsells.some(u => Array.isArray(u?.tags) && u.tags.includes('hasInsurance'));
    return tagHit || pkgHit || upsellHit;
  }, [booking]);

  const [grantKulanzVoucher, setGrantKulanzVoucher] = useState(!hasInsurance);

  const canShow = useMemo(() => {
    if (!booking?._id) return false;
    if (booking.status !== 'noShow') return false;
    if (!hasNoShowInvoiceAdyenMerchant(booking)) return false;
    if (booking.noShowInvoiceSkippedAt) return false;
    if (!booking.noShowInvoiceId && !hasNoShowInvoiceBillableLineItems(booking)) return false;
    return authCtx.checkRightAny('internalBooking:special:sendNoShowInvoice');
  }, [authCtx, booking]);

  if (!canShow) return null;

  const alreadySent = Boolean(booking?.noShowInvoiceId);
  const hasMail = Boolean(booking?.mail);
  const invoiceTimingOk = isNoShowInvoiceSendable(booking);

  const handleOpenConfirm = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id || alreadySent || !hasMail || !invoiceTimingOk) return;
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (isSubmitting) return;
    setIsConfirmOpen(false);
    setGrantKulanzVoucher(!hasInsurance);
  };

  const handleConfirmSend = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id || alreadySent || !hasMail || !isNoShowInvoiceSendable(booking)) return;

    try {
      setIsSubmitting(true);
      const response = await sendNoShowInvoice(booking._id, {
        grantKulanzVoucher: hasInsurance ? false : grantKulanzVoucher,
      });
      if (!response) return;
      if (response?.mailSuppressed) {
        toast.warning(
          'Rechnung erstellt – Mail nicht versendet, da die Empfängeradresse als nicht zustellbar gesperrt ist.'
        );
      } else {
        toast.success('No-Show-Rechnung wurde versendet');
      }
      await onAfterSend?.();
      setIsConfirmOpen(false);
    } catch (err) {
      toast.error(getNoShowInvoiceErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async event => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (!booking?._id) return;
    try {
      setIsResending(true);
      const response = await resendNoShowInvoiceMail(booking._id);
      if (!response) return;
      await onAfterSend?.();
    } catch (err) {
      const code = err?.responseData?.code;
      if (code === 'NO_SHOW_INVOICE_RECIPIENT_SUPPRESSED') {
        toast.error(
          'Empfängeradresse ist weiterhin gesperrt. Bitte zuerst die Adressensperre aufheben oder die Adresse korrigieren.'
        );
      } else {
        toast.error(getNoShowInvoiceErrorMessage(err));
      }
    } finally {
      setIsResending(false);
    }
  };

  // Mail status of the existing NoShow invoice — drives the "Resend" UX.
  const mailSuppressedAt = booking?.noShowInvoiceMailSuppressedAt || null;
  const mailFailedAt = booking?.noShowInvoiceMailFailedAt || null;
  const mailSentAt = booking?.noShowInvoiceMailSentAt || null;
  const mailNotDelivered =
    alreadySent && !mailSentAt && (mailSuppressedAt || mailFailedAt);

  let label = 'No-Show-Rechnung senden';
  if (!hasMail) label = 'Keine E-Mail-Adresse';
  if (alreadySent) label = 'No-Show-Rechnung gesendet';

  const disabledByTiming = !alreadySent && hasMail && !invoiceTimingOk;
  const primaryDisabled = isSubmitting || !hasMail || alreadySent || disabledByTiming;

  const sendButton = (
    <Button
      size={size}
      variant={variant}
      color={alreadySent ? 'success' : 'warning'}
      fullWidth={fullWidth}
      disabled={primaryDisabled}
      onClick={handleOpenConfirm}
      data-ignore-card-click
      sx={sx}
    >
      {label}
    </Button>
  );

  const renderResendBlock = () => {
    if (!mailNotDelivered) return null;
    const reason = mailSuppressedAt
      ? `E-Mail-Adresse war beim ersten Versuch gesperrt${
          mailSuppressedAt
            ? ` (${dayjs(mailSuppressedAt).format('DD.MM.YYYY HH:mm')})`
            : ''
        }`
      : `Versand fehlgeschlagen${
          mailFailedAt ? ` (${dayjs(mailFailedAt).format('DD.MM.YYYY HH:mm')})` : ''
        }${
          booking?.noShowInvoiceMailFailedReason
            ? ` – ${booking.noShowInvoiceMailFailedReason}`
            : ''
        }`;
    return (
      <Box
        sx={{
          mt: 1,
          p: 1,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'error.dark',
          bgcolor: 'rgba(244, 67, 54, 0.08)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          flexDirection: fullWidth ? 'column' : 'row',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexGrow: 1 }}>
          <ReportProblemIcon fontSize='small' sx={{ color: 'error.main' }} />
          <Typography variant='caption' color='error.light'>
            <b>Mail nicht versendet.</b> {reason}.
          </Typography>
        </Box>
        <Button
          size='small'
          variant='outlined'
          color='warning'
          startIcon={<RestartAltIcon />}
          onClick={handleResend}
          disabled={isResending}
          fullWidth={fullWidth}
        >
          {isResending ? 'Wird gesendet…' : 'Erneut senden'}
        </Button>
      </Box>
    );
  };

  return (
    <>
      {disabledByTiming ? (
        <Tooltip
          title={`No-Show-Rechnung erst ab ${NO_SHOW_GRACE_MINUTES_AFTER_START} Minuten nach geplantem Beginn versendbar`}
        >
          <span
            style={fullWidth ? { display: 'block', width: '100%' } : { display: 'inline-block' }}
          >
            {sendButton}
          </span>
        </Tooltip>
      ) : (
        sendButton
      )}
      {renderResendBlock()}

      <Dialog open={isConfirmOpen} onClose={handleCloseConfirm} fullWidth maxWidth="xs">
        <DialogTitle>No-Show-Rechnung versenden?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Soll die No-Show-Rechnung wirklich an <strong>{booking?.mail || ''}</strong> versendet werden?
          </Typography>
          {!hasInsurance && (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={grantKulanzVoucher}
                  onChange={e => setGrantKulanzVoucher(e.target.checked)}
                  disabled={isSubmitting}
                />
              }
              label="Kulanz-Gutschein bei fristgerechter Zahlung gewähren"
            />
          )}
          {hasInsurance && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Da eine Versicherung gebucht wurde, wird kein Kulanz-Gutschein gewährt.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmSend}
            disabled={isSubmitting || !hasMail || alreadySent || !invoiceTimingOk}
            color="warning"
            variant="contained"
          >
            Senden
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default NoShowInvoiceButton;
